import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Shell, CTGrid, StatCard } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft } from "lucide-react";

export default function StudentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get(`/analytics/student/${id}`).then((r) => setData(r.data)); }, [id]);

  const st = data?.student;
  return (
    <Shell title={st?.name || "Student"} subtitle={st ? `${st.class_name} · Division ${st.division} · Roll ${st.roll_no}` : ""} nav={
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
    }>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="student-overall">
        <StatCard label="Tests Taken" value={data?.overall?.attempts ?? 0} testid="sd-attempts" />
        <StatCard label="Questions" value={data?.overall?.total_questions ?? 0} testid="sd-questions" />
        <StatCard label="Correct" value={data?.overall?.correct ?? 0} testid="sd-correct" />
        <StatCard label="Overall %" value={`${data?.overall?.percentage ?? 0}%`} testid="sd-percentage" />
      </div>

      <h3 className="text-xl font-head font-semibold mt-10 mb-4">CT Competency Performance</h3>
      <CTGrid ct={data?.ct} testid="student-ct-grid" />

      <h3 className="text-xl font-head font-semibold mt-10 mb-4">Chapter-wise Performance</h3>
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="chapterwise">
        {(data?.chapters || []).length === 0 && <p className="text-sm text-slate-500">No tests completed yet</p>}
        {(data?.chapters || []).map((c) => (
          <div key={c.chapter_name}>
            <div className="flex justify-between text-sm"><span>{c.chapter_name}</span><span className="font-semibold">{c.percentage}%</span></div>
            <div className="h-2 bg-slate-100 rounded-full mt-1 overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${c.percentage}%` }} /></div>
          </div>
        ))}
      </div>

      <h3 className="text-xl font-head font-semibold mt-10 mb-4">Test-wise Performance</h3>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <Table data-testid="testwise-table">
          <TableHeader><TableRow><TableHead>Test</TableHead><TableHead>Chapter</TableHead><TableHead>Score</TableHead><TableHead>%</TableHead><TableHead>Date</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {(data?.tests || []).map((t) => (
              <TableRow key={t.attempt_id} data-testid={`testwise-row-${t.attempt_id}`}>
                <TableCell>{t.test_title}</TableCell>
                <TableCell>{t.chapter_name}</TableCell>
                <TableCell>{t.correct}/{t.total_questions}</TableCell>
                <TableCell className="font-semibold">{t.percentage}%</TableCell>
                <TableCell>{new Date(t.submitted_at).toLocaleDateString()}</TableCell>
                <TableCell><Link to={`/result/${t.attempt_id}`} className="text-primary text-sm" data-testid={`view-result-${t.attempt_id}`}>View</Link></TableCell>
              </TableRow>
            ))}
            {data?.tests?.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No attempts yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Shell>
  );
}
